import {normalizeFiltersArg} from '@/helpers/filtersArg'

export type QueryType = 'query' | 'mutation';
// Тип для полей, поддерживающий вложенные структуры
export type Field =
  | string
  | {
    [key: string]: Field[] | { args?: Record<string, any>, fields?: Field[] }
  };

// Функция для генерации объектов в формате {name: "value", value: "value"} без кавычек вокруг ключей
function stringifyGraphQLValue(value: any): string {
  if (typeof value === 'object' && value !== null && !Array.isArray(value) && '__enum' in value) {
    return String((value as {__enum: string}).__enum)
  }

  if (typeof value === 'string' && value.startsWith('"""') && value.endsWith('"""')) {
    return value
  }

  if (typeof value === 'string') {
    return JSON.stringify(value)
  }

  if (typeof value === 'boolean' || typeof value === 'number' || value === null) {
    return `${value}`
  }

  if (Array.isArray(value)) {
    const vals = value.map(v => stringifyGraphQLValue(v)).join(', ')
    return `[${vals}]`
  }

  if (typeof value === 'object') {
    return formatObjectWithoutQuotes(value as Record<string, any>)
  }

  return JSON.stringify(value)
}

function formatObjectWithoutQuotes(obj: Record<string, any>): string {
  const body = Object.entries(obj)
    .map(([k, v]) => `${k}: ${stringifyGraphQLValue(v)}`)
    .join(', ')
  return `{ ${body} }`
}

// Генерация параметров в скобках с учетом массивов объектов
export function genReqAttrs(params?: Record<string, any>): string {
  return params && Object.keys(params).length
    ? Object.entries(params)
      .filter(([, value]) => value !== null)
      .map(([key, rawValue]) => {
        const value = key === 'filters' ? normalizeFiltersArg(rawValue) : rawValue
        // Проверка на массив объектов
        if (
          Array.isArray(value) &&
            value.every((item) => typeof item === 'object')
        ) {
          const formattedArray = value
            .map((obj) => formatObjectWithoutQuotes(obj))
            .join(', ')
          return `${key}: [${formattedArray}]`
        }

        // Проверка на строку с GraphQL синтаксисом (старый формат Elasticsearch)
        if (typeof value === 'string' && (value.includes('{') || value.includes('}'))) {
          return `${key}: ${value}`
        }

        // Проверка на объект (новый формат FilterConfiguration)
        if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
          return `${key}: ${stringifyGraphQLValue(value)}`
        }

        // Если обычное значение, обрабатываем стандартно
        return `${key}: ${JSON.stringify(value)}`
      })
      .join(', ')
    : ''
}

// Рекурсивная генерация атрибутов для запроса, включая вложенные структуры и параметры
export function genAttrs(fields: Field[]): string {
  return fields
    .map((field) => {
      if (typeof field === 'string') {
        // Если поле строка, возвращаем его как есть
        return field
      } else {
        return Object.entries(field)
          .map(([key, value]) => {
            if (Array.isArray(value)) {
              const subFields = genAttrs(value)
              // Проверяем, есть ли вложенные поля, прежде чем добавлять {}
              return subFields ? `${key} { ${subFields} }` : key
            } else if (typeof value === 'object') {
              const args = value.args ? `(${genReqAttrs(value.args)})` : ''
              const subFields = value.fields ? genAttrs(value.fields) : ''
              // Если subFields пуст, не добавляем {}
              return `${key}${args}${subFields ? ` { ${subFields} }` : ''}`
            }
            return ''
          })
          .join('\n\t')
      }
    })
    .join('\n\t')
}

// Опциональная генерация атрибутов для пагинации
export function genAttrsPagination(
  dataFields: Field[],
  includePagination: boolean,
): string {
  if (!includePagination) {
    return genAttrs(dataFields)
  }

  const paginationFields = [
    'total',
    'perPage',
    'currentPage',
    'from',
    'to',
    'lastPage',
    'hasMorePages',
  ]

  return `
        ${paginationFields.join('\n\t')}
        data {
            ${genAttrs(dataFields)}
        }
    `
}

// Основная функция для сборки запроса
export function buildQuery(
  type: QueryType,
  name: string,
  dataFields: Field[],
  includePagination: boolean = true,
  params?: Record<string, any>,
): string {
  const reqAttrs = genReqAttrs(params)
  const attrsPagination = genAttrsPagination(dataFields, includePagination)
  const body = !!attrsPagination ? `{\n${attrsPagination}\n}` : ''

  return `
        ${type} ${name.charAt(0).toUpperCase() + name.slice(1)} {
            ${name}${reqAttrs ? `(${reqAttrs})` : ''} ${body}
        }
    `
}

// Описание одного запроса для батчинга
export interface BatchQueryItem {
  name: string
  // Алиас для запроса (позволяет несколько запросов к одному полю с разными параметрами)
  alias?: string
  fields: Field[]
  includePagination?: boolean
  params?: Record<string, any>
}

// Объединение нескольких query в один GraphQL документ
export function buildBatchQuery(queries: BatchQueryItem[]): string {
  const parts = queries.map((q) => {
    const reqAttrs = genReqAttrs(q.params)
    const attrsPagination = genAttrsPagination(q.fields, q.includePagination ?? true)
    const body = !!attrsPagination ? `{\n${attrsPagination}\n}` : ''
    const prefix = q.alias ? `${q.alias}: ${q.name}` : q.name

    return `    ${prefix}${reqAttrs ? `(${reqAttrs})` : ''} ${body}`
  })

  return `query BatchQuery {\n${parts.join('\n')}\n}`
}
